import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PlanInfoCard } from "@/components/account/PlanInfoCard";
import { UpgradePlanCard } from "@/components/account/UpgradePlanCard";
import { useUserProfileData } from "@/hooks/useUserProfileData";
import { buildOwnProfileUpdate } from "@/utils/clientWrites";

type ProfileFormValues = {
  email: string;
  whatsapp: string;
};

export default function Account() {
  const { toast } = useToast();
  const form = useForm<ProfileFormValues>();
  const { profileData, userRole, isLoading, isActive, refetchProfile } = useUserProfileData();

  useEffect(() => {
    if (profileData) {
      form.reset({
        email: profileData.email,
        whatsapp: profileData.whatsapp || "",
      });
    }
  }, [profileData, form, userRole]);

  const onSubmit = async (data: ProfileFormValues) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;

    const updateData = buildOwnProfileUpdate(data.whatsapp);

    const { error } = await supabase
      .from("profiles")
      .update(updateData)
      .eq("id", session.user.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Erro ao atualizar perfil",
        description: error.message,
      });
      return;
    }

    toast({
      title: "Perfil atualizado",
      description: "Suas informações foram atualizadas com sucesso.",
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-pulse flex space-x-4">
          <div className="rounded-full bg-slate-200 h-10 w-10"></div>
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-slate-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">Minha Conta</h1>
            <p className="text-muted-foreground">
              Gerencie suas informações pessoais e assinatura
            </p>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Informações Pessoais</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input {...field} disabled />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="whatsapp"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>WhatsApp</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="+55 (11) 99999-9999" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {userRole === 'admin' && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Função</p>
                      <p className="text-sm text-muted-foreground">Administrador</p>
                    </div>
                  )}

                  <Button type="submit">Salvar alterações</Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <PlanInfoCard 
              planType={profileData?.plan_type || 'basic'}
              isActive={isActive}
              userRole={userRole}
            />
            {profileData?.plan_type === 'basic' && (
              <UpgradePlanCard onUpgradeSuccess={refetchProfile} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}